import { type ReactNode, useId } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import { FieldDescription } from '../../labels/field-description';
import FieldLabel, { fieldLabelStyles } from '../../labels/field-label';
import { ReadOnlyValue as SharedReadOnlyValue } from '../../labels/read-only-value';
import { comboboxChipStyles } from './combobox';
import { MultiCombobox, type MultiComboboxProps } from './multi-combobox';
import { SingleCombobox, type SingleComboboxProps } from './single-combobox';

interface ComboboxFieldProps {
  /** Optional field label rendered above the combobox. Associates with the input via `id` when one is set. */
  label?: string;
  /** Shows the required indicator next to the label when `True` and not read-only. */
  required?: DynamicFormRequiredType;
  readOnly?: boolean;
  /**
   * What a read-only combobox renders.
   * `'input'` (default) keeps the control.
   * `'value'` replaces it with the selection as text.
   */
  readOnlyDisplay?: 'input' | 'value';
  /** Error message rendered below the combobox. When set, the input is also marked `aria-invalid`. */
  error?: string;
  /** Helper text rendered below the combobox and wired to the input via `aria-describedby`. */
  description?: string;
}

// The discriminant lives here rather than on the presets, which know nothing about this union. It is mandatory on
// the multi arm: optional, array-shaped props would match it while the dispatch below fell through to single-select.
export type ComboboxProps<T> = ComboboxFieldProps &
  ((MultiComboboxProps<T> & { multiple: true }) | (SingleComboboxProps<T> & { multiple?: false }));

const ReadOnlyField = ({
  label,
  error,
  description,
  children,
}: Pick<ComboboxFieldProps, 'label' | 'error' | 'description'> & { children: ReactNode }) => {
  return (
    <div className="flex flex-col">
      {label ? (
        <dl>
          <dt className={fieldLabelStyles}>{label}</dt>
          <dd className="m-0">{children}</dd>
        </dl>
      ) : (
        children
      )}
      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

/** The read-only value of a single-select, styled to sit where the input's text was */
const ReadOnlyValue = <T,>({
  id,
  value,
  getLabel,
  itemToStringLabel,
}: Pick<SingleComboboxProps<T>, 'value' | 'getLabel' | 'itemToStringLabel'> & { id?: string }) => {
  const label = value !== null && value !== undefined ? (itemToStringLabel ?? getLabel)(value) : '';

  return (
    <SharedReadOnlyValue id={id} isEmpty={!label}>
      {label}
    </SharedReadOnlyValue>
  );
};

/** The read-only value of a multi-select: chips with no root context or remove control. */
const ReadOnlyChips = <T,>({
  id,
  value = [],
  getKey,
  getLabel,
}: Pick<MultiComboboxProps<T>, 'value' | 'getKey' | 'getLabel'> & { id?: string }) => {
  return (
    <div id={id} className="flex flex-wrap items-center gap-1 py-1">
      {value.length === 0 ? (
        <EmptyFieldLabel />
      ) : (
        value.map((item) => (
          <span key={getKey(item)} className={comboboxChipStyles} data-slot="combobox-chip">
            <span className="truncate">{getLabel(item)}</span>
          </span>
        ))
      )}
    </div>
  );
};

export const Combobox = <T,>({
  label,
  required = DynamicFormRequiredType.False,
  readOnly = false,
  readOnlyDisplay = 'input',
  error,
  description,
  ...modeProps
}: ComboboxProps<T>) => {
  const generatedId = useId();
  // A generated id keeps a built-in label associated with the body when the caller supplies none.
  const identifier = modeProps.id !== undefined ? String(modeProps.id) : label ? generatedId : undefined;

  // The required indicator is suppressed when read-only, so a read-only value needs only the label, value and error.
  if (readOnly && readOnlyDisplay === 'value') {
    return (
      <ReadOnlyField label={label} error={error} description={description}>
        {modeProps.multiple ? (
          <ReadOnlyChips
            id={identifier}
            value={modeProps.value}
            getKey={modeProps.getKey}
            getLabel={modeProps.getLabel}
          />
        ) : (
          <ReadOnlyValue
            id={identifier}
            value={modeProps.value}
            getLabel={modeProps.getLabel}
            itemToStringLabel={modeProps.itemToStringLabel}
          />
        )}
      </ReadOnlyField>
    );
  }

  const ariaInvalid = error !== undefined ? true : modeProps['aria-invalid'];

  const descriptionId = description ? `${identifier ?? generatedId}-description` : undefined;
  const describedBy = modeProps['aria-describedby'] ?? descriptionId;

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        {label && <FieldLabel name={identifier} label={label} />}
        <RequiredIndicator readOnly={readOnly} required={required} />
      </div>
      {modeProps.multiple ? (
        <MultiCombobox
          {...(modeProps as MultiComboboxProps<T>)}
          id={identifier}
          readOnly={readOnly}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
        />
      ) : (
        <SingleCombobox
          {...(modeProps as SingleComboboxProps<T>)}
          id={identifier}
          readOnly={readOnly}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
        />
      )}
      {description && (
        <FieldDescription id={descriptionId} className="py-1">
          {description}
        </FieldDescription>
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};
